/**
 * ConfirmDialog / NoticeDialog — the app's in-page replacements for the browser's
 * confirm() and alert().
 *
 * A native confirm() freezes the tab, ignores the app's styling entirely, and reads
 * to an anxious owner like a browser error rather than something their cat's record
 * asked them. Every destructive confirmation now goes through this instead.
 *
 * The `useConfirm` hook keeps call sites almost identical to what they replaced:
 *
 *   const { confirm, notify, dialog } = useConfirm();
 *   ...
 *   if (!(await confirm({ title: 'Delete this weight entry?' }))) return;
 *   ...
 *   return <>{dialog}...</>;
 *
 * Built on the shared <Modal>, so focus containment, Escape-to-close and the
 * backdrop behave the same as every other overlay in the app.
 */

import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { Button } from './Button';

export interface ConfirmOptions {
  /** The question. Phrased as the action, e.g. "Revoke this care pass?". */
  title: string;
  /** What will happen. Optional — omit when the title says everything. */
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for anything destructive (the default for a confirm). */
  variant?: 'danger' | 'primary';
}

export interface NoticeOptions {
  title: string;
  message?: string;
  closeLabel?: string;
}

interface ConfirmDialogProps extends ConfirmOptions {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  return (
    <Modal isOpen={isOpen} onClose={onCancel} size="sm">
      <h2 className="text-base font-semibold text-charcoal">{title}</h2>
      {message && <p className="mt-2 text-sm text-mid">{message}</p>}
      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onCancel}>
          {cancelLabel ?? t('shared.confirmDialog.cancel')}
        </Button>
        <Button variant={variant} onClick={onConfirm}>
          {confirmLabel ?? t('shared.confirmDialog.confirm')}
        </Button>
      </div>
    </Modal>
  );
};

interface NoticeDialogProps extends NoticeOptions {
  isOpen: boolean;
  onClose: () => void;
}

/** A one-button message — what alert() was being used for. */
export const NoticeDialog: React.FC<NoticeDialogProps> = ({
  isOpen,
  title,
  message,
  closeLabel,
  onClose,
}) => {
  const { t } = useTranslation();
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <h2 className="text-base font-semibold text-charcoal">{title}</h2>
      {message && <p className="mt-2 text-sm text-mid">{message}</p>}
      <div className="mt-5 flex justify-end">
        <Button onClick={onClose}>{closeLabel ?? t('shared.confirmDialog.ok')}</Button>
      </div>
    </Modal>
  );
};

type PendingConfirm = { kind: 'confirm'; options: ConfirmOptions };
type PendingNotice = { kind: 'notice'; options: NoticeOptions };

export interface UseConfirmResult {
  /** Resolves true if the owner confirmed, false if they cancelled or dismissed. */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** Resolves once the message has been acknowledged. */
  notify: (options: NoticeOptions) => Promise<void>;
  /** Render this somewhere in the component's tree. */
  dialog: React.ReactNode;
}

export function useConfirm(): UseConfirmResult {
  const [pending, setPending] = useState<PendingConfirm | PendingNotice | null>(null);
  // The promise's resolver for whichever dialog is open. Held in a ref so opening a
  // dialog never re-renders on the resolver's identity.
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const settle = useCallback((value: boolean) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setPending(null);
    resolve?.(value);
  }, []);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        setPending({ kind: 'confirm', options });
      }),
    [],
  );

  const notify = useCallback(
    (options: NoticeOptions) =>
      new Promise<void>((resolve) => {
        resolveRef.current = () => resolve();
        setPending({ kind: 'notice', options });
      }),
    [],
  );

  const dialog =
    pending?.kind === 'confirm' ? (
      <ConfirmDialog
        isOpen
        {...pending.options}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    ) : pending?.kind === 'notice' ? (
      <NoticeDialog isOpen {...pending.options} onClose={() => settle(false)} />
    ) : null;

  return { confirm, notify, dialog };
}
